import React from 'react';
import {
  Box,
  Grid,
  Card,
  CardContent,
  Typography,
  Avatar,
  Chip,
  List,
  ListItem,
  ListItemText,
  Button,
  LinearProgress,
  useTheme,
  alpha,
} from '@mui/material';
import {
  Engineering as EngineeringIcon,
  Build as BuildIcon,
  Warning as WarningIcon,
  CheckCircle as CheckIcon,
  Speed as SpeedIcon,
  TrendingUp as TrendingUpIcon,
} from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import { AreaChart, Area, XAxis, YAxis, Tooltip, ResponsiveContainer, BarChart, Bar } from 'recharts';

const OperacionesDashboard = () => {
  const theme = useTheme();
  const navigate = useNavigate();

  const quickStats = [
    { title: 'Obras Activas', value: '5', icon: <EngineeringIcon />, color: theme.palette.primary.main },
    { title: 'OT Abiertas', value: '23', icon: <BuildIcon />, color: theme.palette.warning.main },
    { title: 'Equipos Operativos', value: '42', icon: <SpeedIcon />, color: theme.palette.success.main },
    { title: 'Incidentes HSE (2026)', value: '2', icon: <WarningIcon />, color: theme.palette.info.main },
  ];

  const avanceObras = [
    { obra: 'Pozo XY-123', avance: 78, estado: 'En Tiempo' },
    { obra: 'Pozo AB-456', avance: 45, estado: 'En Tiempo' },
    { obra: 'Workover MZ-789', avance: 92, estado: 'Por Finalizar' },
    { obra: 'Perforación NW-012', avance: 18, estado: 'En Tiempo' },
  ];

  const produccionData = [
    { mes: 'Oct', produccion: 12500 },
    { mes: 'Nov', produccion: 13200 },
    { mes: 'Dic', produccion: 14100 },
    { mes: 'Ene', produccion: 13800 },
  ];

  const alertasOperativas = [
    { texto: 'Mantenimiento preventivo: Bomba BOP-003', tipo: 'Mantenimiento', fecha: '05/02/2026' },
    { texto: 'Checklist HSE pendiente: OT-2026-045', tipo: 'HSE', fecha: 'Hoy' },
    { texto: 'Inspección equipo: Grúa GR-012', tipo: 'Inspección', fecha: '10/02/2026' },
  ];

  return (
    <Box className="fade-in">
      <Box sx={{ mb: 4, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <Box>
          <Typography variant="h4" fontWeight={700} color="primary">
            Operaciones
          </Typography>
          <Typography variant="body1" color="text.secondary">
            Control operativo y HSE
          </Typography>
        </Box>
        <Button variant="contained" startIcon={<BuildIcon />} onClick={() => navigate('/operaciones/ordenes-trabajo')}>
          Nueva Orden de Trabajo
        </Button>
      </Box>

      {/* Quick Stats */}
      <Grid container spacing={3} sx={{ mb: 4 }}>
        {quickStats.map((stat, index) => (
          <Grid item xs={6} sm={3} key={index}>
            <Card>
              <CardContent>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                  <Box>
                    <Typography variant="h4" fontWeight={700}>
                      {stat.value}
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      {stat.title}
                    </Typography>
                  </Box>
                  <Avatar sx={{ bgcolor: alpha(stat.color, 0.1), color: stat.color }}>
                    {stat.icon}
                  </Avatar>
                </Box>
              </CardContent>
            </Card>
          </Grid>
        ))}
      </Grid>

      <Grid container spacing={3}>
        {/* Avance de Obras */}
        <Grid item xs={12} lg={6}>
          <Card sx={{ height: '100%' }}>
            <CardContent>
              <Typography variant="h6" fontWeight={600} gutterBottom>
                Avance de Obras
              </Typography>
              {avanceObras.map((obra, index) => (
                <Box key={index} sx={{ mb: 2 }}>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 0.5 }}>
                    <Typography variant="body2" fontWeight={500}>{obra.obra}</Typography>
                    <Chip
                      size="small"
                      label={`${obra.avance}%`}
                      color={obra.avance > 90 ? 'success' : obra.avance > 50 ? 'info' : 'warning'}
                    />
                  </Box>
                  <LinearProgress
                    variant="determinate"
                    value={obra.avance}
                    sx={{
                      height: 8,
                      borderRadius: 4,
                      bgcolor: alpha(theme.palette.grey[400], 0.2),
                    }}
                  />
                </Box>
              ))}
            </CardContent>
          </Card>
        </Grid>

        {/* Producción */}
        <Grid item xs={12} lg={6}>
          <Card sx={{ height: '100%' }}>
            <CardContent>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                <Typography variant="h6" fontWeight={600}>
                  Producción (bbl/día)
                </Typography>
                <Chip icon={<TrendingUpIcon />} label="+4.2%" color="success" size="small" />
              </Box>
              <ResponsiveContainer width="100%" height={200}>
                <AreaChart data={produccionData}>
                  <defs>
                    <linearGradient id="colorProd" x1="0" y1="0" x2="0" y2="1">
                      <stop offset="5%" stopColor={theme.palette.primary.main} stopOpacity={0.3} />
                      <stop offset="95%" stopColor={theme.palette.primary.main} stopOpacity={0} />
                    </linearGradient>
                  </defs>
                  <XAxis dataKey="mes" />
                  <YAxis />
                  <Tooltip formatter={(value) => `${value.toLocaleString()} bbl`} />
                  <Area type="monotone" dataKey="produccion" stroke={theme.palette.primary.main} fill="url(#colorProd)" strokeWidth={2} />
                </AreaChart>
              </ResponsiveContainer>
            </CardContent>
          </Card>
        </Grid>

        {/* Alertas Operativas */}
        <Grid item xs={12}>
          <Card>
            <CardContent>
              <Typography variant="h6" fontWeight={600} gutterBottom>
                Alertas Operativas
              </Typography>
              <Grid container spacing={2}>
                {alertasOperativas.map((alerta, index) => (
                  <Grid item xs={12} md={4} key={index}>
                    <Box
                      sx={{
                        p: 2,
                        borderRadius: 2,
                        bgcolor: alpha(theme.palette.warning.main, 0.05),
                        border: `1px solid ${alpha(theme.palette.warning.main, 0.2)}`,
                      }}
                    >
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                        <WarningIcon color="warning" fontSize="small" />
                        <Chip label={alerta.tipo} size="small" variant="outlined" />
                      </Box>
                      <Typography variant="body2">{alerta.texto}</Typography>
                      <Typography variant="caption" color="text.secondary">{alerta.fecha}</Typography>
                    </Box>
                  </Grid>
                ))}
              </Grid>
            </CardContent>
          </Card>
        </Grid>
      </Grid>
    </Box>
  );
};

export default OperacionesDashboard;
